import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

type TimeDisplayProps = {
  /** Navy sidebar needs light text; default uses theme primary/secondary. */
  variant?: 'default' | 'sidebar';
};

/**
 * Local and UTC clocks, updating every second (same format as Ignite frontend).
 */
export function TimeDisplay({ variant = 'default' }: TimeDisplayProps) {
  const [currentTime, setCurrentTime] = useState<Dayjs>(() => dayjs());

  useEffect(() => {
    const id = setInterval(() => setCurrentTime(dayjs()), 1000);
    return () => clearInterval(id);
  }, []);

  const formattedLocalTime = useMemo(
    () => currentTime.format('hh:mm:ss A - MMM-D, YYYY'),
    [currentTime],
  );
  const formattedUTCTime = useMemo(
    () => currentTime.utc().format('hh:mm:ss A - MMM-D, YYYY'),
    [currentTime],
  );

  const isSidebar = variant === 'sidebar';
  const labelWidth = 40;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography
        variant="body2"
        sx={{
          color: isSidebar ? 'rgba(255, 255, 255, 0.92)' : 'primary.main',
          fontSize: isSidebar ? '0.75rem' : undefined,
          lineHeight: 1.35,
        }}
      >
        <Box
          component="span"
          sx={{ display: 'inline-block', width: labelWidth, fontWeight: 600 }}
        >
          Local:
        </Box>{' '}
        {formattedLocalTime}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: isSidebar ? 'rgba(255, 183, 140, 0.95)' : 'secondary.main',
          fontSize: isSidebar ? '0.75rem' : undefined,
          lineHeight: 1.35,
        }}
      >
        <Box
          component="span"
          sx={{ display: 'inline-block', width: labelWidth, fontWeight: 600 }}
        >
          UTC:
        </Box>{' '}
        {formattedUTCTime}
      </Typography>
    </Box>
  );
}

export default TimeDisplay;
